import Avatar from './Avatar'
import AvatarController from './AvatarController'
import MovingAverage from '../../helpers/MovingAverage'
import { MoveEvent, MoveEventHelper } from '../../io/events'
import {
  Point,
  Rect,
  Vector,
  vectorAngle,
  vectorFromPoints
} from '../BasicTypes'

export interface PlayerState {
  readonly position: Point
  readonly spriteImage: string
  readonly region: Rect
  readonly anchor: Point
  readonly nameOffset: Point
  readonly dialogueOffset: Point
}

export default class PlayerController {
  get state (): PlayerState {
    return this._state
  }
  speed: number

  private _state: PlayerState
  private readonly movingSmoothPeriod: number

  private position: Point
  private lastMoveStartedPosition: Point | null = null
  private lastMoveElasped: number | null = null
  private lastMove: MoveEvent | null = null
  private lastMoveHelper: MoveEventHelper | null = null
  private lastPosition: Point
  /// Current position before moving average filter
  private rawPosition: Point
  private avatarController: AvatarController
  private readonly movingSpeed: (deltaVector: Vector, elasped: number) => number

  // moving average for x and y of point
  private readonly maX: MovingAverage
  private readonly maY: MovingAverage

  constructor ({
    position,
    speed,
    avatar,
    movingSpeed,
    movingSmoothPeriod = 32
  }: {
    position: Point
    speed: number
    avatar: Avatar
    movingSpeed: (deltaVector: Vector, elasped: number) => number
    movingSmoothPeriod?: number
  }) {
    this.movingSmoothPeriod = movingSmoothPeriod
    this.avatarController = new AvatarController({ avatar, speed })
    this.maX = new MovingAverage(this.movingSmoothPeriod)
    this.maY = new MovingAverage(this.movingSmoothPeriod)

    this.position = position
    this.speed = speed
    this.lastPosition = position
    this.rawPosition = position
    this.movingSpeed = movingSpeed
    this.maX.reset(position.x)
    this.maY.reset(position.y)
    this._state = this.currentState()
  }

  move (event: MoveEvent) {
    // Notice: we use the raw position here, otherwise using the position
    // after MA filter will end up creating a gap of starting point between
    // previous moving event
    this.lastMoveStartedPosition = this.rawPosition
    this.lastMoveElasped = 0
    this.lastMove = event
    this.lastMoveHelper = new MoveEventHelper(event)
  }

  updateFrame (elasped: number) {
    // is the state dirty?
    let dirty = false
    if (this.lastMoveHelper) {
      const moveElapsed = this.lastMoveElasped! + elasped
      const currentPosition = this.lastMoveHelper.currentPosition(
        moveElapsed,
        this.lastMoveStartedPosition!
      )
      this.maX.add(currentPosition.x)
      this.maY.add(currentPosition.y)
      const lastPosition = this.position
      this.lastMoveElasped = moveElapsed
      this.position = {
        x: this.maX.value,
        y: this.maY.value
      }
      dirty = true
      this.rawPosition = currentPosition
      this.lastPosition = lastPosition
    }
    const deltaVector = vectorFromPoints(this.lastPosition, this.position)
    if (deltaVector.dx !== 0 || deltaVector.dy !== 0) {
      const speed = this.movingSpeed(deltaVector, elasped)
      const angle = vectorAngle(deltaVector)
      this.avatarController.act('walk', angle, speed)
    } else {
      const speed = this.speed
      this.avatarController.act('stand', this.avatarController.angle, speed)
    }
    const frame = this.avatarController.frame
    this.avatarController.updateFrame(elasped)
    if (frame !== this.avatarController.frame) {
      dirty = true
    }
    // dirty state, let's update it
    if (dirty) {
      this._state = this.currentState()
    }
  }

  private currentState (): PlayerState {
    return {
      position: this.position,
      spriteImage: this.avatarController.avatar.imageURL,
      region: this.avatarController.frame.region,
      anchor: this.avatarController.frame.anchor,
      nameOffset: this.avatarController.frame.nameOffset,
      dialogueOffset: this.avatarController.frame.dialogueOffset
    }
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/models/MapObjects/PlayerController.ts